import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import type { NavItem } from '../../types';

interface IconRailProps {
  items: NavItem[];
  activeGroupId: string | null;
  collapsed: boolean;
  onGroupSelect: (id: string) => void;
}

/**
 * Per-module icon mapping. Sidebar nodes carry an `icon` string sourced
 * from the live `/api/Menu/all` payload, which uses Material-Icons naming
 * (e.g. `dashboard`, `business`, `inventory_2`). We translate to a
 * curated Lucide icon. PascalCase Lucide names also work as a fallback
 * so legacy fixtures keep rendering.
 */
const iconMap: Record<string, Icons.LucideIcon> = {
  // Lucide PascalCase (legacy fixtures + sidebarNav.json)
  LayoutDashboard: Icons.LayoutDashboard,
  LayoutGrid: Icons.LayoutGrid,
  Users: Icons.Users,
  Table: Icons.Table,
  IndianRupee: Icons.IndianRupee,
  BarChart3: Icons.BarChart3,
  Calendar: Icons.Calendar,
  LifeBuoy: Icons.LifeBuoy,
  Settings: Icons.Settings,
  Package: Icons.Package,
  Layers: Icons.Layers,
  Store: Icons.Store,
  Warehouse: Icons.Warehouse,
  ShoppingCart: Icons.ShoppingCart,
  UserCog: Icons.UserCog,
  Tag: Icons.Tag,
  Truck: Icons.Truck,
  Receipt: Icons.Receipt,
  Calculator: Icons.Calculator,
  Square: Icons.Square,
  Database: Icons.Database,
  ReceiptText: Icons.ReceiptText,
  UtensilsCrossed: Icons.UtensilsCrossed,
  HeartHandshake: Icons.HeartHandshake,
  Factory: Icons.Factory,
  Building: Icons.Building,
  Sparkles: Icons.Sparkles,
  GitBranch: Icons.GitBranch,
  FileText: Icons.FileText,
  Bell: Icons.Bell,
  ShieldCheck: Icons.ShieldCheck,
  Building2: Icons.Building2,
  Plug: Icons.Plug,
  UserCircle: Icons.UserCircle,

  // Material-Icons names returned by /api/Menu/all (HRMS API).
  dashboard: Icons.LayoutDashboard,
  business: Icons.Building2,
  account_tree: Icons.GitBranch,
  apartment: Icons.Building,
  badge: Icons.IdCard,
  storefront: Icons.Store,
  warehouse: Icons.Warehouse,
  people: Icons.Users,
  list: Icons.List,
  how_to_reg: Icons.UserPlus,
  manage_accounts: Icons.UserCog,
  logout: Icons.LogOut,
  schedule: Icons.Clock,
  event_busy: Icons.CalendarX,
  event_note: Icons.CalendarDays,
  event_available: Icons.CalendarCheck,
  event: Icons.Calendar,
  celebration: Icons.PartyPopper,
  fact_check: Icons.ClipboardCheck,
  access_time: Icons.Timer,
  flight: Icons.Plane,
  payments: Icons.Wallet,
  paid: Icons.BadgeIndianRupee,
  tune: Icons.SlidersHorizontal,
  description: Icons.FileText,
  receipt_long: Icons.Receipt,
  request_quote: Icons.ReceiptText,
  receipt: Icons.Receipt,
  work: Icons.Briefcase,
  group: Icons.Users,
  groups: Icons.Users,
  mail: Icons.Mail,
  trending_up: Icons.TrendingUp,
  flag: Icons.Flag,
  star_rate: Icons.Star,
  analytics: Icons.BarChart3,
  school: Icons.GraduationCap,
  inventory_2: Icons.Package,
  inventory: Icons.Package,
  add_box: Icons.PlusSquare,
  category: Icons.Layers,
  label: Icons.Tag,
  sell: Icons.Tag,
  straighten: Icons.Ruler,
  sync_alt: Icons.RefreshCw,
  compare_arrows: Icons.ArrowLeftRight,
  point_of_sale: Icons.ShoppingCart,
  shopping_cart: Icons.ShoppingCart,
  shopping_basket: Icons.ShoppingBasket,
  undo: Icons.Undo2,
  local_shipping: Icons.Truck,
  account_balance: Icons.Landmark,
  book: Icons.BookOpen,
  edit_note: Icons.PencilLine,
  menu_book: Icons.BookText,
  percent: Icons.Percent,
  assessment: Icons.BarChart3,
  gavel: Icons.Gavel,
  settings: Icons.Settings,
  person: Icons.User,
  admin_panel_settings: Icons.ShieldCheck,
  menu: Icons.Menu,
  lock: Icons.Lock,
  credit_card: Icons.CreditCard,
  corporate_fare: Icons.Building2,
  campaign: Icons.Megaphone,
  history: Icons.History,
};

function resolveIcon(name: string | undefined): Icons.LucideIcon {
  if (!name) return Icons.Square;
  if (iconMap[name]) return iconMap[name];
  // Try a normalised match for stragglers ("Group" → "group").
  const lower = name.toLowerCase();
  if (iconMap[lower]) return iconMap[lower];
  // Strip trailing _<digit> ("inventory_2" → "inventory").
  const root = lower.replace(/_\d+$/, '');
  if (iconMap[root]) return iconMap[root];
  return Icons.Square;
}

export default function IconRail({ items, activeGroupId, collapsed, onGroupSelect }: IconRailProps) {
  // Real i18n binding — falls back to the static label when a key is missing.
  const { t: tn } = useTranslation('panel');
  const t = (key: string, fallback?: string) => {
    const v = tn(key);
    return v === key && fallback ? fallback : v;
  };
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="flex h-full w-16 flex-col border-r border-border bg-surface-sidebar">
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-3">
        {items.map((item) => {
          const Icon = resolveIcon(item.icon);
          const isActive = activeGroupId === item.id;
          const isCurrentRoute = item.path && location.pathname === item.path;

          return (
            <button
              key={item.id}
              title={t(item.labelKey, item.label)}
              onClick={() => {
                onGroupSelect(item.id);
                if (item.path && !item.children?.length) {
                  navigate(item.path);
                }
              }}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg transition-colors mx-auto',
                isActive || isCurrentRoute
                  ? 'bg-primary text-primary-foreground'
                  : 'text-content-muted hover:bg-surface-card-hover hover:text-content-primary'
              )}
            >
              <Icon size={20} />
            </button>
          );
        })}
      </nav>
    </div>
  );
}
